// ============================================
// api/services.ts — API service functions for all modules
// ============================================
import apiClient from './apiClient';
import type {
  User, District, PACSEntity, Season, LoanSanction, Bank, UtilizationHead,
  DistrictDrawdown, DistrictUtilization, DistrictFarmers, DashboardSummary,
} from '../types/markfed';

// ─── Auth ─────────────────────────────────────────
export const authAPI = {
  login: (email: string, password: string) =>
    apiClient.post<{ access_token: string; refresh_token: string; user: User }>('/auth/login', { email, password }),
  refresh: (refreshToken: string) =>
    apiClient.post<{ access_token: string }>('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => apiClient.post('/auth/logout'),
  me: () => apiClient.get<User>('/auth/me'),
  changePassword: (oldPassword: string, newPassword: string) =>
    apiClient.post<{ message: string; access_token: string; refresh_token: string }>('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    }),
};

// ─── Gunnies proforma & actor-write endpoints ─────
export interface GunnyProformaRow {
  id: number;
  sl_no: number;
  district: string;
  merchant: string;
  tender_id: string | null;
  society_name: string | null;
  bags_supplied: number;
  bags_utilized: number;
  bags_returned: number;
  debit_note_bags: number;
  supplier_invoice_no: string | null;
  supplier_invoice_date: string | null;
  eway_bill_no: string | null;
  bill_submission_date: string | null;
  bill_raised_bags: number;
  claimed_rate_per_bag_incl_gst: number | null;
  claimed_net_amount: number | null;
  claimed_cgst: number | null;
  claimed_sgst: number | null;
  claimed_gross_amount: number | null;
  certified_bags: number;
  rate_per_bag_incl_gst: number | null;
  net_amount: number | null;
  cgst: number | null;
  sgst: number | null;
  gross_amount: number | null;
  condition_society: string | null;
  condition_warehouse: string | null;
  condition_dm: string | null;
  mp_certified_bags: number | null;
  mp_rate_per_bag_incl_gst: number | null;
  mp_net_amount: number | null;
  mp_cgst: number | null;
  mp_sgst: number | null;
  mp_gross_amount: number | null;
  payment_released: number;
  balance_to_release: number;
  last_paid_on: string | null;
  timeline: {
    society_done_at: string | null;
    supplier_claim_at: string | null;
    dm_verified_at: string | null;
    mp_approved_at: string | null;
    cao_approved_at: string | null;
    md_approved_at: string | null;
    last_paid_on: string | null;
  };
  district_id: number;
  supplier_id: number;
  pacs_id: number | null;
  godown_id: number | null;
  return_godown_id: number | null;
  status: string;
}

export const reportsAPI = {
  gunniesProforma: (params: { season_id: number; commodity_id?: number; district_id?: number }) =>
    apiClient.get<{
      season: { id: number; name: string } | null;
      commodity: { id: number; name: string } | null;
      row_count: number;
      rows: GunnyProformaRow[];
    }>('/reports/gunnies-proforma', { params }),
  gunniesProformaXlsx: (params: { season_id: number; commodity_id?: number; district_id?: number }) =>
    apiClient.get('/reports/gunnies-proforma', {
      params: { ...params, format: 'xlsx' },
      responseType: 'blob',
    }),
  gunnyReconciliation: (params: { season_id: number; district_id?: number }) =>
    apiClient.get<{
      season: { id: number; name: string } | null;
      row_count: number;
      rows: GunnyReconciliationRow[];
    }>('/reports/gunny-reconciliation', { params }),
  gunnyReconciliationXlsx: (params: { season_id: number; district_id?: number }) =>
    apiClient.get('/reports/gunny-reconciliation', {
      params: { ...params, format: 'xlsx' },
      responseType: 'blob',
    }),
};

export interface GunnyPoDelivery {
  id: number;
  po_id: number;
  godown_id: number;
  bags: number;
  delivered_on: string;
  received_by_user_id: number | null;
  remarks: string | null;
  created_at?: string;
  godown?: {
    id: number;
    godown_name: string;
    godown_type: string | null;
    capacity_mts: number | null;
  };
}

export interface GunnyReconciliationRow {
  sl_no: number;
  supplier_id: number;
  supplier_name: string;
  tender_id: string | null;
  district_id: number;
  district_name: string;
  godown_id: number | null;
  godown_name: string;
  godown_type: string | null;
  capacity_mts: number | null;
  is_pool: boolean;
  po_bags: number;
  po_count: number;
  delivered: number;
  allotted: number;
  utilized: number;
  returned: number;
  allotment_count: number;
  on_hand: number | null;
  on_hand_pct: number | null;
  capacity_use_pct: number | null;
}


// ─── Users (SUPER_ADMIN) ─────────────────────────
export const usersAPI = {
  list: () => apiClient.get<User[]>('/users'),
  get: (id: number) => apiClient.get<User>(`/users/${id}`),
  create: (data: Partial<User> & { password: string }) => apiClient.post<User>('/users', data),
  update: (id: number, data: Partial<User>) => apiClient.patch<User>(`/users/${id}`, data),
  deactivate: (id: number) => apiClient.delete(`/users/${id}`),
  resetPassword: (id: number) => apiClient.post(`/users/${id}/reset-password`),
  // Auto-provision a society/supplier login for an existing master row.
  // Returns { user, temp_password } — the temp_password is shown to the
  // operator once and the user must change it on first login.
  provisionSociety: (pacs_id: number) =>
    apiClient.post<{ user: User; temp_password: string }>('/users/provision-society', { pacs_id }),
  provisionSupplier: (supplier_id: number) =>
    apiClient.post<{ user: User; temp_password: string }>('/users/provision-supplier', { supplier_id }),
  // Scoped password reset — same role gates and district scope as provisioning.
  resetSocietyPassword: (pacs_id: number) =>
    apiClient.post<{ user: { id: number; email: string }; temp_password: string }>('/users/reset-society-password', { pacs_id }),
  resetSupplierPassword: (supplier_id: number) =>
    apiClient.post<{ user: { id: number; email: string }; temp_password: string }>('/users/reset-supplier-password', { supplier_id }),
};

// ─── Districts ────────────────────────────────────
export const districtsAPI = {
  list: () => apiClient.get<District[]>('/districts'),
  get: (id: number) => apiClient.get<District>(`/districts/${id}`),
  create: (data: Partial<District>) => apiClient.post<District>('/districts', data),
  update: (id: number, data: Partial<District>) => apiClient.patch<District>(`/districts/${id}`, data),
  delete: (id: number) => apiClient.delete(`/districts/${id}`),
};

// ─── PACS Entities ────────────────────────────────
export const pacsAPI = {
  list: (districtId?: number) =>
    apiClient.get<PACSEntity[]>('/pacs', { params: districtId ? { district_id: districtId } : {} }),
  get: (id: number) => apiClient.get<PACSEntity>(`/pacs/${id}`),
  create: (data: Partial<PACSEntity>) => apiClient.post<PACSEntity>('/pacs', data),
  update: (id: number, data: Partial<PACSEntity>) => apiClient.patch<PACSEntity>(`/pacs/${id}`, data),
  delete: (id: number) => apiClient.delete(`/pacs/${id}`),
};

// ─── Banks ────────────────────────────────────────
export const banksAPI = {
  list: (params?: { search?: string; is_active?: boolean }) =>
    apiClient.get<Bank[]>('/banks', { params }),
  get: (id: number) => apiClient.get<Bank>(`/banks/${id}`),
  create: (data: Partial<Bank>) => apiClient.post<Bank>('/banks', data),
  update: (id: number, data: Partial<Bank>) => apiClient.patch<Bank>(`/banks/${id}`, data),
  delete: (id: number) => apiClient.delete(`/banks/${id}`),
};

// ─── Utilization Heads ────────────────────────────
export const utilizationHeadsAPI = {
  list: (params?: { search?: string; is_active?: boolean }) =>
    apiClient.get<UtilizationHead[]>('/utilization-heads', { params }),
  get: (id: number) => apiClient.get<UtilizationHead>(`/utilization-heads/${id}`),
  create: (data: Partial<UtilizationHead>) => apiClient.post<UtilizationHead>('/utilization-heads', data),
  update: (id: number, data: Partial<UtilizationHead>) => apiClient.patch<UtilizationHead>(`/utilization-heads/${id}`, data),
  delete: (id: number) => apiClient.delete(`/utilization-heads/${id}`),
};

// ─── Seasons ──────────────────────────────────────
export const seasonsAPI = {
  list: () => apiClient.get<Season[]>('/seasons'),
  active: () => apiClient.get<Season>('/seasons/active'),
  get: (id: number) => apiClient.get<Season>(`/seasons/${id}`),
  create: (data: Partial<Season>) => apiClient.post<Season>('/seasons', data),
  update: (id: number, data: Partial<Season>) => apiClient.patch<Season>(`/seasons/${id}`, data),
  delete: (id: number) => apiClient.delete(`/seasons/${id}`),
};

// ─── Loan Sanction (AO_CAO) ──────────────────────
export const loanSanctionAPI = {
  list: (seasonId: number) => apiClient.get<LoanSanction[]>(`/loan-sanction/${seasonId}`),
  create: (seasonId: number, data: Partial<LoanSanction>) =>
    apiClient.post<LoanSanction>(`/loan-sanction/${seasonId}`, data),
  update: (seasonId: number, id: number, data: Partial<LoanSanction>) =>
    apiClient.patch<LoanSanction>(`/loan-sanction/${seasonId}/${id}`, data),
  delete: (seasonId: number, id: number) =>
    apiClient.delete(`/loan-sanction/${seasonId}/${id}`),
  submit: (seasonId: number, id: number) =>
    apiClient.post(`/loan-sanction/${seasonId}/${id}/submit`),
  approve: (seasonId: number, id: number) =>
    apiClient.post(`/loan-sanction/${seasonId}/${id}/approve`),
  reject: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/loan-sanction/${seasonId}/${id}/reject`, { reason }),
};

// ─── District Drawdowns (AO_CAO) ─────────────────
export const drawdownsAPI = {
  list: (seasonId: number) => apiClient.get<DistrictDrawdown[]>(`/drawdowns/${seasonId}`),
  getByDistrict: (seasonId: number, districtId: number) =>
    apiClient.get<DistrictDrawdown[]>(`/drawdowns/${seasonId}/${districtId}`),
  create: (seasonId: number, data: Partial<DistrictDrawdown>) =>
    apiClient.post<DistrictDrawdown>(`/drawdowns/${seasonId}`, data),
  update: (seasonId: number, drawdownId: number, data: Partial<DistrictDrawdown>) =>
    apiClient.patch<DistrictDrawdown>(`/drawdowns/${seasonId}/${drawdownId}`, data),
  delete: (seasonId: number, drawdownId: number) =>
    apiClient.delete(`/drawdowns/${seasonId}/${drawdownId}`),
  submit: (seasonId: number, drawdownId: number) =>
    apiClient.post(`/drawdowns/${seasonId}/${drawdownId}/submit`),
  approve: (seasonId: number, drawdownId: number) =>
    apiClient.post(`/drawdowns/${seasonId}/${drawdownId}/approve`),
  reject: (seasonId: number, drawdownId: number, reason: string) =>
    apiClient.post(`/drawdowns/${seasonId}/${drawdownId}/reject`, { reason }),
};

// ─── District Utilization (DM) ───────────────────
export const utilizationAPI = {
  listAll: (seasonId: number) =>
    apiClient.get<DistrictUtilization[]>(`/utilization/${seasonId}`),
  listByDistrict: (seasonId: number, districtId: number) =>
    apiClient.get<DistrictUtilization[]>(`/utilization/${seasonId}/district/${districtId}`),
  get: (seasonId: number, id: number) =>
    apiClient.get<DistrictUtilization>(`/utilization/${seasonId}/record/${id}`),
  create: (seasonId: number, data: any) =>
    apiClient.post<DistrictUtilization>(`/utilization/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) =>
    apiClient.patch<DistrictUtilization>(`/utilization/${seasonId}/${id}`, data),
  delete: (seasonId: number, id: number) =>
    apiClient.delete(`/utilization/${seasonId}/${id}`),
  submit: (seasonId: number, id: number) =>
    apiClient.post(`/utilization/${seasonId}/${id}/submit`),
  approve: (seasonId: number, id: number) =>
    apiClient.post(`/utilization/${seasonId}/${id}/approve`),
  reject: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/utilization/${seasonId}/${id}/reject`, { reason }),
};

// ─── District Farmers (DM) ───────────────────────
export const farmersAPI = {
  listAll: (seasonId: number, commodityId?: number) =>
    apiClient.get<DistrictFarmers[]>(`/farmers/${seasonId}`, { params: commodityId ? { commodityId } : {} }),
  listByDistrict: (seasonId: number, districtId: number) =>
    apiClient.get<DistrictFarmers[]>(`/farmers/${seasonId}/district/${districtId}`),
  get: (seasonId: number, id: number) =>
    apiClient.get<DistrictFarmers>(`/farmers/${seasonId}/record/${id}`),
  create: (seasonId: number, data: any) =>
    apiClient.post<DistrictFarmers>(`/farmers/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) =>
    apiClient.patch<DistrictFarmers>(`/farmers/${seasonId}/${id}`, data),
  delete: (seasonId: number, id: number) =>
    apiClient.delete(`/farmers/${seasonId}/${id}`),
  submit: (seasonId: number, id: number) =>
    apiClient.post(`/farmers/${seasonId}/${id}/submit`),
  approve: (seasonId: number, id: number) =>
    apiClient.post(`/farmers/${seasonId}/${id}/approve`),
  reject: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/farmers/${seasonId}/${id}/reject`, { reason }),
};

// ─── Dashboard ────────────────────────────────────
export const dashboardAPI = {
  summary: (seasonId: number) =>
    apiClient.get<DashboardSummary>(`/dashboard/summary/${seasonId}`),
  districtSummary: (seasonId: number, districtId: number) =>
    apiClient.get(`/dashboard/district/${seasonId}/${districtId}`),
};

// ─── Reconciliation ──────────────────────────────
export const reconciliationAPI = {
  variance: (seasonId: number) => apiClient.get(`/reconciliation/variance/${seasonId}`),
  commodity: (seasonId: number) => apiClient.get(`/reconciliation/commodity/${seasonId}`),
  status: (seasonId: number) => apiClient.get(`/reconciliation/status/${seasonId}`),
  bills: (seasonId: number, commodityId?: number) =>
    apiClient.get(`/reconciliation/bills/${seasonId}`, { params: commodityId ? { commodity_id: commodityId } : {} }),
  // Utilization reconciliation — per-district received vs spent breakdown across
  // all 11 heads (5 computed from proformas + 6 DM-entered), plus grand totals.
  utilization: (seasonId: number) =>
    apiClient.get<any>(`/reconciliation/utilization/${seasonId}`),
};

// ─── Commodities ─────────────────────────────────
export const commoditiesAPI = {
  list: () => apiClient.get<any[]>('/commodities'),
  get: (id: number) => apiClient.get<any>(`/commodities/${id}`),
  create: (data: any) => apiClient.post('/commodities', data),
  update: (id: number, data: any) => apiClient.patch(`/commodities/${id}`, data),
  delete: (id: number) => apiClient.delete(`/commodities/${id}`),
};

// ─── Season Commodities ─────────────────────────
export const seasonCommoditiesAPI = {
  list: (seasonId: number) => apiClient.get<any[]>(`/season-commodities/${seasonId}`),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/season-commodities/${seasonId}/${id}`),
  getByComm: (seasonId: number, commodityId: number) => apiClient.get<any>(`/season-commodities/${seasonId}/commodity/${commodityId}`),
  create: (seasonId: number, data: any) => apiClient.post(`/season-commodities/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/season-commodities/${seasonId}/${id}`, data),
  delete: (seasonId: number, id: number) => apiClient.delete(`/season-commodities/${seasonId}/${id}`),
};

// ─── Procurement Centres ────────────────────────
export const procurementCentresAPI = {
  list: (scId: number) => apiClient.get<any[]>(`/procurement-centres/${scId}`),
  listByDistrict: (scId: number, districtId: number) => apiClient.get<any[]>(`/procurement-centres/${scId}/district/${districtId}`),
  get: (scId: number, id: number) => apiClient.get<any>(`/procurement-centres/${scId}/${id}`),
  create: (scId: number, data: any) => apiClient.post(`/procurement-centres/${scId}`, data),
  update: (scId: number, id: number, data: any) => apiClient.patch(`/procurement-centres/${scId}/${id}`, data),
  delete: (scId: number, id: number) => apiClient.delete(`/procurement-centres/${scId}/${id}`),
};

// ─── District Production ────────────────────────
export const districtProductionAPI = {
  listAll: (seasonId?: number) => apiClient.get<any[]>(`/district-production/all`, { params: seasonId ? { seasonId } : {} }),
  list: (scId: number) => apiClient.get<any[]>(`/district-production/${scId}`),
  get: (scId: number, districtId: number) => apiClient.get<any>(`/district-production/${scId}/${districtId}`),
  upsert: (scId: number, data: any) => apiClient.post(`/district-production/${scId}`, data),
  submit: (scId: number, id: number) => apiClient.patch(`/district-production/${scId}/${id}/submit`),
  verify: (scId: number, id: number) => apiClient.patch(`/district-production/${scId}/${id}/verify`),
  approve: (scId: number, id: number) => apiClient.patch(`/district-production/${scId}/${id}/approve`),
  reject: (scId: number, id: number, reason: string) => apiClient.patch(`/district-production/${scId}/${id}/reject`, { reason }),
};

// ─── Procurement Tracking ───────────────────────
export const procurementTrackingAPI = {
  list: (scId: number) => apiClient.get<any[]>(`/procurement-tracking/${scId}`),
  get: (scId: number, id: number) => apiClient.get<any>(`/procurement-tracking/${scId}/${id}`),
  summary: (scId: number) => apiClient.get<any>(`/procurement-tracking/${scId}/summary`),
  create: (scId: number, data: any) => apiClient.post(`/procurement-tracking/${scId}`, data),
  update: (scId: number, id: number, data: any) => apiClient.patch(`/procurement-tracking/${scId}/${id}`, data),
  submit: (scId: number, id: number) => apiClient.patch(`/procurement-tracking/${scId}/${id}/submit`),
  verify: (scId: number, id: number) => apiClient.patch(`/procurement-tracking/${scId}/${id}/verify`),
  approve: (scId: number, id: number) => apiClient.patch(`/procurement-tracking/${scId}/${id}/approve`),
  reject: (scId: number, id: number, reason: string) => apiClient.patch(`/procurement-tracking/${scId}/${id}/reject`, { reason }),
};

// ─── Godowns ────────────────────────────────────
export const godownsAPI = {
  list: (districtId?: number) => apiClient.get<any[]>('/godowns', { params: districtId ? { district_id: districtId } : {} }),
  get: (id: number) => apiClient.get<any>(`/godowns/${id}`),
  create: (data: any) => apiClient.post('/godowns', data),
  update: (id: number, data: any) => apiClient.patch(`/godowns/${id}`, data),
  delete: (id: number) => apiClient.delete(`/godowns/${id}`),
};

// ─── H&T Contractors ────────────────────────────
export const htContractorsAPI = {
  list: () => apiClient.get<any[]>('/ht-contractors'),
  get: (id: number) => apiClient.get<any>(`/ht-contractors/${id}`),
  create: (data: any) => apiClient.post('/ht-contractors', data),
  update: (id: number, data: any) => apiClient.patch(`/ht-contractors/${id}`, data),
  delete: (id: number) => apiClient.delete(`/ht-contractors/${id}`),
};

// ─── Unloading Contractors (labor-only, distinct from H&T) ────────
export const unloadingContractorsAPI = {
  list: () => apiClient.get<any[]>('/unloading-contractors'),
  get: (id: number) => apiClient.get<any>(`/unloading-contractors/${id}`),
  create: (data: any) => apiClient.post('/unloading-contractors', data),
  update: (id: number, data: any) => apiClient.patch(`/unloading-contractors/${id}`, data),
  delete: (id: number) => apiClient.delete(`/unloading-contractors/${id}`),
};

// ─── Godown Contractors ─────────────────────────
export const godownContractorsAPI = {
  list: (seasonId: number) => apiClient.get<any[]>(`/godown-contractors/${seasonId}`),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/godown-contractors/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post(`/godown-contractors/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/godown-contractors/${seasonId}/${id}`, data),
  delete: (seasonId: number, id: number) => apiClient.delete(`/godown-contractors/${seasonId}/${id}`),
};

// ─── Unloading Bills ────────────────────────────
export const unloadingBillsAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; district_id?: number }) =>
    apiClient.get<any[]>(`/unloading-bills/${seasonId}`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/unloading-bills/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post(`/unloading-bills/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/unloading-bills/${seasonId}/${id}`, data),
  certify: (seasonId: number, id: number) => apiClient.post(`/unloading-bills/${seasonId}/${id}/certify`),
  verify: (seasonId: number, id: number, data: any) => apiClient.post(`/unloading-bills/${seasonId}/${id}/verify`, data),
  approve: (seasonId: number, id: number, data: any) => apiClient.post(`/unloading-bills/${seasonId}/${id}/approve`, data),
  reject: (seasonId: number, id: number, reason: string) => apiClient.post(`/unloading-bills/${seasonId}/${id}/reject`, { reason }),
  uploadInvoice: (seasonId: number, id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`/unloading-bills/${seasonId}/${id}/invoice`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  invoiceUrl: (seasonId: number, id: number) => `/unloading-bills/${seasonId}/${id}/invoice`,
  deleteInvoice: (seasonId: number, id: number) => apiClient.delete(`/unloading-bills/${seasonId}/${id}/invoice`),
  aggregate: (seasonId: number, id: number) => apiClient.post(`/unloading-bills/${seasonId}/${id}/aggregate`),
  linkEntries: (seasonId: number, id: number, entryIds: number[]) =>
    apiClient.post(`/unloading-bills/${seasonId}/${id}/link-entries`, { entry_ids: entryIds }),
};

// ─── Transport Trips ────────────────────────────
export const transportTripsAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; district_id?: number; status?: string }) =>
    apiClient.get<any[]>(`/transport-trips/${seasonId}`, { params }),
  listLinkable: (
    seasonId: number,
    params: { commodity_id: number; district_id: number; contractor_id: number; bill_id?: number; period_start?: string; period_end?: string },
  ) => apiClient.get<any[]>(`/transport-trips/${seasonId}/linkable`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/transport-trips/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post(`/transport-trips/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/transport-trips/${seasonId}/${id}`, data),
  submit: (seasonId: number, id: number) => apiClient.post(`/transport-trips/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number, data: any) => apiClient.post(`/transport-trips/${seasonId}/${id}/verify`, data),
  approve: (seasonId: number, id: number, data: any) => apiClient.post(`/transport-trips/${seasonId}/${id}/approve`, data),
  reject: (seasonId: number, id: number, reason: string) => apiClient.post(`/transport-trips/${seasonId}/${id}/reject`, { reason }),
};

// ─── Transport Bills ────────────────────────────
export const transportBillsAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; district_id?: number; status?: string }) =>
    apiClient.get<any[]>(`/transport-bills/${seasonId}`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/transport-bills/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post(`/transport-bills/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/transport-bills/${seasonId}/${id}`, data),
  delete: (seasonId: number, id: number) => apiClient.delete(`/transport-bills/${seasonId}/${id}`),
  aggregate: (seasonId: number, id: number) => apiClient.post(`/transport-bills/${seasonId}/${id}/aggregate`),
  linkTrips: (seasonId: number, id: number, tripIds: number[]) =>
    apiClient.post(`/transport-bills/${seasonId}/${id}/link-trips`, { trip_ids: tripIds }),
  uploadInvoice: (seasonId: number, id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`/transport-bills/${seasonId}/${id}/invoice`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  invoiceUrl: (seasonId: number, id: number) => `/transport-bills/${seasonId}/${id}/invoice`,
  deleteInvoice: (seasonId: number, id: number) => apiClient.delete(`/transport-bills/${seasonId}/${id}/invoice`),
  submit: (seasonId: number, id: number) => apiClient.post(`/transport-bills/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number, data: any) => apiClient.post(`/transport-bills/${seasonId}/${id}/verify`, data),
  approve: (seasonId: number, id: number, data: any) => apiClient.post(`/transport-bills/${seasonId}/${id}/approve`, data),
  reject: (seasonId: number, id: number, reason: string) => apiClient.post(`/transport-bills/${seasonId}/${id}/reject`, { reason }),
};

// ─── Unloading Entries (per-batch, mirror of Transport Trips) ────
export const unloadingEntriesAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; district_id?: number }) =>
    apiClient.get<any[]>(`/unloading-entries/${seasonId}`, { params }),
  listLinkable: (
    seasonId: number,
    params: { commodity_id: number; district_id: number; contractor_id: number; bill_id?: number; period_start?: string; period_end?: string },
  ) => apiClient.get<any[]>(`/unloading-entries/${seasonId}/linkable`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/unloading-entries/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post(`/unloading-entries/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/unloading-entries/${seasonId}/${id}`, data),
  submit: (seasonId: number, id: number) => apiClient.post(`/unloading-entries/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number, data: any) => apiClient.post(`/unloading-entries/${seasonId}/${id}/verify`, data),
  approve: (seasonId: number, id: number, data: any) => apiClient.post(`/unloading-entries/${seasonId}/${id}/approve`, data),
  reject: (seasonId: number, id: number, reason: string) => apiClient.post(`/unloading-entries/${seasonId}/${id}/reject`, { reason }),
};

// ─── Gunny Suppliers ────────────────────────────
export const gunnySuppliersAPI = {
  list: (districtId?: number) => apiClient.get<any[]>('/gunny-suppliers', { params: districtId ? { district_id: districtId } : {} }),
  get: (id: number) => apiClient.get<any>(`/gunny-suppliers/${id}`),
  create: (data: any) => apiClient.post('/gunny-suppliers', data),
  update: (id: number, data: any) => apiClient.patch(`/gunny-suppliers/${id}`, data),
  delete: (id: number) => apiClient.delete(`/gunny-suppliers/${id}`),
};

// ─── Gunny Supplier Rates (rate master per supplier × bag_type) ──
export const gunnySupplierRatesAPI = {
  list: (supplierId?: number) =>
    apiClient.get<any[]>('/gunny-supplier-rates', { params: supplierId ? { supplier_id: supplierId } : {} }),
  create: (data: { supplier_id: number; bag_type: string; rate_per_bag: number; is_active?: boolean }) =>
    apiClient.post('/gunny-supplier-rates', data),
  update: (id: number, data: { rate_per_bag?: number; is_active?: boolean }) =>
    apiClient.patch(`/gunny-supplier-rates/${id}`, data),
  delete: (id: number) => apiClient.delete(`/gunny-supplier-rates/${id}`),
};

// ─── Gunny Utilization ──────────────────────────
export const gunnyUtilizationAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; district_id?: number }) =>
    apiClient.get<any[]>(`/gunny-utilization/${seasonId}`, { params }),
  // PO-centric fulfillment view that drives /gunny-bags/utilization
  listByPo: (seasonId: number) =>
    apiClient.get<any[]>(`/gunny-utilization/${seasonId}/by-po`),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/gunny-utilization/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post(`/gunny-utilization/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch(`/gunny-utilization/${seasonId}/${id}`, data),
  submit: (seasonId: number, id: number) => apiClient.post(`/gunny-utilization/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number, data: any) => apiClient.post(`/gunny-utilization/${seasonId}/${id}/verify`, data),
  approve: (seasonId: number, id: number, data: any) => apiClient.post(`/gunny-utilization/${seasonId}/${id}/approve`, data),
  reject: (seasonId: number, id: number, reason: string) => apiClient.post(`/gunny-utilization/${seasonId}/${id}/reject`, { reason }),
  // Receipt verification gate — DM signs off on bags_supplied count before
  // society can submit / utilize / return.
  verifyReceipt: (seasonId: number, id: number) =>
    apiClient.post(`/gunny-utilization/${seasonId}/${id}/verify-receipt`),
  rejectReceipt: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/gunny-utilization/${seasonId}/${id}/reject-receipt`, { reason }),
  correctReceipt: (seasonId: number, id: number, body: { bags_supplied: number; remarks?: string }) =>
    apiClient.patch(`/gunny-utilization/${seasonId}/${id}/correct-receipt`, body),
  // DM bulk-forwards every ready consignment under one PO into MP/CAO review.
  forwardAllForPo: (seasonId: number, poId: number) =>
    apiClient.post<{ submitted: number[]; skipped: { id: number; reason: string }[]; total: number }>(
      `/gunny-utilization/${seasonId}/po/${poId}/forward-all`,
    ),
  // Post-MP gates.
  caoApprove: (seasonId: number, id: number) =>
    apiClient.post(`/gunny-utilization/${seasonId}/${id}/cao-approve`),
  mdApprove: (seasonId: number, id: number) =>
    apiClient.post(`/gunny-utilization/${seasonId}/${id}/md-approve`),
  // External actors — scoped writes
  societyConfirm: (
    seasonId: number,
    id: number,
    body: { bags_utilized?: number; bags_returned?: number; condition_society?: string },
  ) => apiClient.patch(`/gunny-utilization/${seasonId}/${id}/society-confirm`, body),
  supplierClaim: (
    seasonId: number,
    id: number,
    body: {
      supplier_invoice_no?: string;
      supplier_invoice_date?: string;
      eway_bill_no?: string;
      bill_submission_date?: string;
      bill_raised_bags?: number;
      claimed_rate_per_bag_incl_gst?: number;
    },
  ) => apiClient.patch(`/gunny-utilization/${seasonId}/${id}/supplier-claim`, body),
  mpApprove: (
    seasonId: number,
    id: number,
    body: { mp_certified_bags?: number; mp_rate_per_bag_incl_gst?: number },
  ) => apiClient.patch(`/gunny-utilization/${seasonId}/${id}/mp-approve`, body),
  dmCertify: (
    seasonId: number,
    id: number,
    body: {
      certified_bags?: number;
      rate_per_bag_incl_gst?: number;
      condition_warehouse?: string;
      condition_dm?: string;
    },
  ) => apiClient.patch(`/gunny-utilization/${seasonId}/${id}/dm-certify`, body),
  audit: (seasonId: number, id: number) =>
    apiClient.get<GunnyAuditEntry[]>(`/gunny-utilization/${seasonId}/${id}/audit`),
  reopen: (seasonId: number, id: number, reason?: string) =>
    apiClient.post(`/gunny-utilization/${seasonId}/${id}/reopen`, { reason }),
  returnGodown: (seasonId: number, id: number, godown_id: number | null) =>
    apiClient.patch(`/gunny-utilization/${seasonId}/${id}/return-godown`, { godown_id }),
};

export interface GunnyAuditEntry {
  id: number;
  gunny_utilization_id: number;
  action:
    | 'allot' | 'society_confirm' | 'supplier_claim'
    | 'dm_certify' | 'mp_approve' | 'reject' | 'reopen' | 'return_update'
    | 'payment_add' | 'payment_update' | 'payment_remove';
  actor_user_id: number | null;
  actor_role: string | null;
  actor?: { id: number; name: string; email: string; role: string } | null;
  from_status: string | null;
  to_status: string | null;
  changed_fields: Record<string, any> | null;
  details: string | null;
  created_at: string;
}

// ─── Historical Procurement ─────────────────────
export const historicalProcurementAPI = {
  list: (commodityId?: number) => apiClient.get<any[]>('/historical-procurement', { params: commodityId ? { commodity_id: commodityId } : {} }),
  byCommodity: (commodityId: number) => apiClient.get<any[]>(`/historical-procurement/commodity/${commodityId}`),
  bulkCreate: (data: any[]) => apiClient.post('/historical-procurement/bulk', data),
};

// ─── DPR ────────────────────────────────────────
export const dprAPI = {
  summary: (seasonId: number) => apiClient.get<any>(`/dpr/${seasonId}`),
  commodity: (seasonId: number, commodityId: number) => apiClient.get<any>(`/dpr/${seasonId}/commodity/${commodityId}`),
};

// ─── Dashboard (extended) ───────────────────────
export const dashboardCommoditiesAPI = {
  commodities: (seasonId: number) => apiClient.get<any>(`/dashboard/commodities/${seasonId}`),
  procurementStatus: (seasonId: number) => apiClient.get<any>(`/dashboard/procurement-status/${seasonId}`),
};

// ─── Import ─────────────────────────────────────
export const importAPI = {
  preview: (file: File, maxRows?: number) => {
    const fd = new FormData();
    fd.append('file', file);
    if (maxRows) fd.append('maxRows', String(maxRows));
    return apiClient.post<any>('/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  excel: (seasonId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<any>(`/import/excel/${seasonId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  pacs: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<any>(`/import/pacs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  godowns: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post<any>(`/import/godowns`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  htContractors: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post<any>(`/import/ht-contractors`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  unloadingContractors: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post<any>(`/import/unloading-contractors`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  gunnySuppliers: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post<any>(`/import/gunny-suppliers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  vehicles: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post<any>(`/import/vehicles`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  transportSlabs: (file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post<any>(`/import/transport-slabs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  dpr: (seasonId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<any>(`/import/dpr/${seasonId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ─── Storage Bills ──────────────────────────────
export const storageBillsAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; godown_id?: number }) =>
    apiClient.get<any[]>(`/storage-bills/${seasonId}`, { params }),
  summary: (seasonId: number, params?: { commodity_id?: number; godown_id?: number }) =>
    apiClient.get<any>(`/storage-bills/${seasonId}/summary`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/storage-bills/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post<any>(`/storage-bills/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch<any>(`/storage-bills/${seasonId}/${id}`, data),
  remove: (seasonId: number, id: number) => apiClient.delete(`/storage-bills/${seasonId}/${id}`),
  submit: (seasonId: number, id: number) => apiClient.post(`/storage-bills/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number) => apiClient.post(`/storage-bills/${seasonId}/${id}/verify`),
  approve: (seasonId: number, id: number) => apiClient.post(`/storage-bills/${seasonId}/${id}/approve`),
  reject: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/storage-bills/${seasonId}/${id}/reject`, { reason }),
  uploadInvoice: (seasonId: number, id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`/storage-bills/${seasonId}/${id}/invoice`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  invoiceUrl: (seasonId: number, id: number) => `/storage-bills/${seasonId}/${id}/invoice`,
  deleteInvoice: (seasonId: number, id: number) => apiClient.delete(`/storage-bills/${seasonId}/${id}/invoice`),
};

// ─── Release Orders ─────────────────────────────
export const releaseOrdersAPI = {
  list: (seasonId: number, params?: { commodity_id?: number; status?: string }) =>
    apiClient.get<any[]>(`/release-orders/${seasonId}`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/release-orders/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post<any>(`/release-orders/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch<any>(`/release-orders/${seasonId}/${id}`, data),
  remove: (seasonId: number, id: number) => apiClient.delete(`/release-orders/${seasonId}/${id}`),
  submit: (seasonId: number, id: number) => apiClient.post(`/release-orders/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number) => apiClient.post(`/release-orders/${seasonId}/${id}/verify`),
  approve: (seasonId: number, id: number) => apiClient.post(`/release-orders/${seasonId}/${id}/approve`),
  reject: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/release-orders/${seasonId}/${id}/reject`, { reason }),
  close: (seasonId: number, id: number) => apiClient.post(`/release-orders/${seasonId}/${id}/close`),
};

// ─── Vehicles (compliance master) ───────────────
export const vehiclesAPI = {
  list: (params?: { status?: string; contractor_id?: number; expiring_soon?: boolean }) =>
    apiClient.get<any[]>('/vehicles', { params }),
  lookup: (vehicleNo: string) =>
    apiClient.get<any>('/vehicles/lookup', { params: { vehicle_no: vehicleNo } }),
  get: (id: number) => apiClient.get<any>(`/vehicles/${id}`),
  create: (data: any) => apiClient.post<any>('/vehicles', data),
  update: (id: number, data: any) => apiClient.patch<any>(`/vehicles/${id}`, data),
  remove: (id: number) => apiClient.delete(`/vehicles/${id}`),
};

// ─── Transport Slabs (distance-based rate master) ─
export const transportSlabsAPI = {
  list: (params?: { commodity_id?: number; is_active?: boolean }) =>
    apiClient.get<any[]>('/transport-slabs', { params }),
  lookup: (km: number, commodityId?: number) =>
    apiClient.get<any>('/transport-slabs/lookup', {
      params: { km, ...(commodityId ? { commodity_id: commodityId } : {}) },
    }),
  get: (id: number) => apiClient.get<any>(`/transport-slabs/${id}`),
  create: (data: any) => apiClient.post<any>('/transport-slabs', data),
  update: (id: number, data: any) => apiClient.patch<any>(`/transport-slabs/${id}`, data),
  remove: (id: number) => apiClient.delete(`/transport-slabs/${id}`),
};

// ─── Gunny Purchase Orders ─────────────────────
export const gunnyPosAPI = {
  remaining: (seasonId: number, params?: { supplier_id?: number; district_id?: number }) =>
    apiClient.get<any[]>(`/gunny-pos/${seasonId}/remaining`, { params }),
  list: (seasonId: number, params?: { supplier_id?: number; status?: string }) =>
    apiClient.get<any[]>(`/gunny-pos/${seasonId}`, { params }),
  get: (seasonId: number, id: number) => apiClient.get<any>(`/gunny-pos/${seasonId}/${id}`),
  create: (seasonId: number, data: any) => apiClient.post<any>(`/gunny-pos/${seasonId}`, data),
  update: (seasonId: number, id: number, data: any) => apiClient.patch<any>(`/gunny-pos/${seasonId}/${id}`, data),
  remove: (seasonId: number, id: number) => apiClient.delete(`/gunny-pos/${seasonId}/${id}`),
  submit: (seasonId: number, id: number) => apiClient.post(`/gunny-pos/${seasonId}/${id}/submit`),
  verify: (seasonId: number, id: number) => apiClient.post(`/gunny-pos/${seasonId}/${id}/verify`),
  approve: (seasonId: number, id: number) => apiClient.post(`/gunny-pos/${seasonId}/${id}/approve`),
  reject: (seasonId: number, id: number, reason: string) =>
    apiClient.post(`/gunny-pos/${seasonId}/${id}/reject`, { reason }),
  fulfill: (seasonId: number, id: number) => apiClient.post(`/gunny-pos/${seasonId}/${id}/fulfill`),
  allot: (
    seasonId: number,
    id: number,
    body: { pacs_id: number; commodity_id: number; bags: number; society_name?: string; godown_id?: number },
  ) => apiClient.post<{
    utilization: any;
    po_summary: {
      po_id: number; po_number: string; po_total_bags: number;
      previously_allotted: number; just_allotted: number; remaining: number;
    };
  }>(`/gunny-pos/${seasonId}/${id}/allot`, body),
  bulkAllot: (
    seasonId: number,
    id: number,
    body: {
      commodity_id: number;
      godown_id?: number;
      allocations: { pacs_id: number; bags: number; society_name?: string; godown_id?: number }[];
    },
  ) => apiClient.post<{
    utilizations: any[];
    po_summary: {
      po_id: number; po_number: string; po_total_bags: number;
      previously_allotted: number; just_allotted: number; remaining: number;
    };
  }>(`/gunny-pos/${seasonId}/${id}/bulk-allot`, body),
  listDeliveries: (seasonId: number, id: number) =>
    apiClient.get<GunnyPoDelivery[]>(`/gunny-pos/${seasonId}/${id}/deliveries`),
  recordDelivery: (
    seasonId: number,
    id: number,
    body: { godown_id: number; bags: number; delivered_on: string; remarks?: string },
  ) => apiClient.post<{
    delivery: GunnyPoDelivery;
    po_summary: {
      po_id: number; po_number: string; po_total_bags: number;
      previously_delivered: number; just_delivered: number; remaining_to_deliver: number;
    };
  }>(`/gunny-pos/${seasonId}/${id}/deliveries`, body),
  removeDelivery: (seasonId: number, id: number, deliveryId: number) =>
    apiClient.delete(`/gunny-pos/${seasonId}/${id}/deliveries/${deliveryId}`),
  uploadInvoice: (seasonId: number, id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`/gunny-pos/${seasonId}/${id}/invoice`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  invoiceUrl: (seasonId: number, id: number) => `/gunny-pos/${seasonId}/${id}/invoice`,
  deleteInvoice: (seasonId: number, id: number) => apiClient.delete(`/gunny-pos/${seasonId}/${id}/invoice`),
  downloadPoPdf: (seasonId: number, id: number) =>
    apiClient.get(`/gunny-pos/${seasonId}/${id}/po-pdf`, { responseType: 'blob' }),
  fulfillment: (seasonId: number, id: number) =>
    apiClient.get<any>(`/gunny-pos/${seasonId}/${id}/fulfillment`),
  incomingForSociety: (seasonId: number) =>
    apiClient.get<any[]>(`/gunny-pos/incoming/${seasonId}`),
  rejectDelivery: (seasonId: number, poId: number, deliveryId: number, body: { reason: string }) =>
    apiClient.post(`/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/reject`, body),
  acknowledgeDelivery: (seasonId: number, poId: number, deliveryId: number, body: { received_bags: number; received_remarks?: string }) =>
    apiClient.post(`/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/acknowledge`, body),
  recordDispatch: (seasonId: number, id: number, body: {
    pacs_id?: number; po_item_id?: number; godown_id?: number; bags: number; delivered_on: string;
    vehicle_number?: string; dispatch_date?: string;
    supplier_invoice_no?: string; supplier_invoice_date?: string;
    eway_bill_no?: string; condition_at_dispatch?: string; remarks?: string;
  }) => apiClient.post<any>(`/gunny-pos/${seasonId}/${id}/deliveries`, body),
  uploadDeliveryInvoice: (seasonId: number, poId: number, deliveryId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post(`/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/invoices`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listDeliveryInvoices: (seasonId: number, poId: number, deliveryId: number) =>
    apiClient.get<any[]>(`/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/invoices`),
  deliveryInvoiceUrl: (seasonId: number, poId: number, deliveryId: number, invoiceId: number) =>
    `/gunny-pos/${seasonId}/${poId}/deliveries/${deliveryId}/invoices/${invoiceId}`,
};

export const gunnyTransfersAPI = {
  list: (seasonId: number, params?: { status?: string }) =>
    apiClient.get<any[]>(`/gunny-transfers/${seasonId}`, { params }),
  available: (utilizationId: number) =>
    apiClient.get<any>(`/gunny-transfers/available/${utilizationId}`),
  create: (seasonId: number, body: { source_utilization_id: number; dest_pacs_id: number; bags: number; transfer_date?: string; reason?: string; specification?: string; bag_type?: string }) =>
    apiClient.post(`/gunny-transfers/${seasonId}`, body),
  review: (id: number, body: { status: 'approved' | 'rejected'; rejection_reason?: string }) =>
    apiClient.patch(`/gunny-transfers/${id}/review`, body),
  cancel: (id: number) => apiClient.patch(`/gunny-transfers/${id}/cancel`, {}),
};

export const gunnyRequestsAPI = {
  list: (seasonId: number, params?: { status?: string; pacs_id?: number; district_id?: number }) =>
    apiClient.get<any[]>(`/gunny-requests/${seasonId}`, { params }),
  create: (seasonId: number, body: {
    requested_bags?: number;
    bag_type?: string;
    specification?: string;
    notes?: string;
    commodity_id?: number;
    required_by_date?: string;
    delivery_godown_id?: number;
    items?: Array<{ bag_type?: string; specification?: string; requested_bags: number; commodity_id?: number }>;
  }) => apiClient.post(`/gunny-requests/${seasonId}`, body),
  review: (seasonId: number, id: number, body: {
    dm_approved_bags?: number;
    items?: Array<{ id: number; dm_approved_bags: number }>;
    status?: string;
    rejection_reason?: string;
  }) => apiClient.patch(`/gunny-requests/${seasonId}/${id}/review`, body),
  bundle: (seasonId: number, body: {
    supplier_id: number; po_number: string; po_date: string; delivery_date?: string;
    delivery_location?: string; gst_rate?: number; advance_pct?: number;
    request_ids: number[];
  }) => apiClient.post(`/gunny-requests/${seasonId}/bundle-into-po`, body),
  shortClosePo: (poId: number, closure_reason: string) =>
    apiClient.post(`/gunny-requests/po/${poId}/short-close`, { closure_reason }),
  poNumberPreview: (seasonId: number, district_id: number, po_date: string) =>
    apiClient.get<{ po_number: string }>(`/gunny-requests/${seasonId}/po-number-preview`, { params: { district_id, po_date } }),
};

// ─── Service Payment Installments (credit-first pattern) ──────────
// Same 1:N shape as farmer_payments but for the 4 service-provider bill types.
// Every write auto-recomputes the parent bill's payment_released + balance_to_release.
export const transportBillPaymentsAPI = {
  list: (transportBillId: number) => apiClient.get<any[]>(`/transport-bill-payments/${transportBillId}`),
  create: (transportBillId: number, data: any) => apiClient.post<any>(`/transport-bill-payments/${transportBillId}`, data),
  update: (id: number, data: any) => apiClient.patch<any>(`/transport-bill-payments/${id}`, data),
  remove: (id: number) => apiClient.delete(`/transport-bill-payments/${id}`),
};

export const unloadingBillPaymentsAPI = {
  list: (unloadingBillId: number) => apiClient.get<any[]>(`/unloading-bill-payments/${unloadingBillId}`),
  create: (unloadingBillId: number, data: any) => apiClient.post<any>(`/unloading-bill-payments/${unloadingBillId}`, data),
  update: (id: number, data: any) => apiClient.patch<any>(`/unloading-bill-payments/${id}`, data),
  remove: (id: number) => apiClient.delete(`/unloading-bill-payments/${id}`),
};

export const gunnyUtilizationPaymentsAPI = {
  list: (gunnyUtilizationId: number) => apiClient.get<any[]>(`/gunny-utilization-payments/${gunnyUtilizationId}`),
  create: (gunnyUtilizationId: number, data: any) => apiClient.post<any>(`/gunny-utilization-payments/${gunnyUtilizationId}`, data),
  update: (id: number, data: any) => apiClient.patch<any>(`/gunny-utilization-payments/${id}`, data),
  remove: (id: number) => apiClient.delete(`/gunny-utilization-payments/${id}`),
};

export const storageBillPaymentsAPI = {
  list: (storageBillId: number) => apiClient.get<any[]>(`/storage-bill-payments/${storageBillId}`),
  create: (storageBillId: number, data: any) => apiClient.post<any>(`/storage-bill-payments/${storageBillId}`, data),
  update: (id: number, data: any) => apiClient.patch<any>(`/storage-bill-payments/${id}`, data),
  remove: (id: number) => apiClient.delete(`/storage-bill-payments/${id}`),
};

// ─── Farmer Payments (installments) ─────────────
export const farmerPaymentsAPI = {
  list: (districtFarmersId: number) =>
    apiClient.get<any[]>(`/farmer-payments/${districtFarmersId}`),
  create: (districtFarmersId: number, data: any) =>
    apiClient.post<any>(`/farmer-payments/${districtFarmersId}`, data),
  update: (id: number, data: any) =>
    apiClient.patch<any>(`/farmer-payments/${id}`, data),
  remove: (id: number) =>
    apiClient.delete(`/farmer-payments/${id}`),
};

// ─── Export ───────────────────────────────────────
export const exportAPI = {
  excel: (seasonId: number, districtId?: number) => {
    const url = districtId
      ? `/export/excel/${seasonId}/${districtId}`
      : `/export/excel/${seasonId}`;
    return apiClient.get(url, { responseType: 'blob' });
  },
  pdf: (seasonId: number) =>
    apiClient.get(`/export/pdf/${seasonId}`, { responseType: 'blob' }),
  dpr: (seasonId: number) =>
    apiClient.get(`/export/dpr/${seasonId}`, { responseType: 'blob' }),
  commodity: (seasonId: number, commodityId: number) =>
    apiClient.get(`/export/commodity/${seasonId}/${commodityId}`, { responseType: 'blob' }),
};
